import { Component, ElementRef, HostListener, OnDestroy, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { DxDrawerComponent } from 'devextreme-angular/ui/drawer';
import { DxResizableComponent } from 'devextreme-angular/ui/resizable';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { trigger } from 'devextreme/events';
import { OptionChangedEvent } from 'devextreme/ui/drawer';
import { InitializedEvent, ResizeEndEvent } from 'devextreme/ui/resizable';
import { ThomasAppConfig } from './thomas-app.config';
import { ThomasChatComponent } from './thomas-chat.component';
import { ThomasConversationService } from './thomas-conversation.service';

const WIDTH_KEY = 'thomas-dock-width';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- shared Thomas markup uses the js- prefix in every app
  selector: 'js-thomas-dock',
  templateUrl: './thomas-dock.component.html',
  styleUrl: './thomas-dock.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [DxDrawerComponent, DxResizableComponent, DxTemplateDirective, ThomasChatComponent]
})
export class ThomasDockComponent implements OnDestroy {

  readonly defaultWidth = 440;
  readonly minWidth = 320;
  private readonly widthCap = 800;
  readonly animationDuration = 250;
  openedStateMode: 'shrink' | 'overlap' = 'shrink';
  width = this.defaultWidth;
  maxWidth = this.defaultWidth;

  @ViewChild(DxDrawerComponent) private drawer?: DxDrawerComponent;
  @ViewChild(DxResizableComponent) private resizable?: DxResizableComponent;
  @ViewChild(ThomasChatComponent) private chat?: ThomasChatComponent;
  private modeTimer?: ReturnType<typeof setTimeout>;
  private preferredWidth = this.storedWidth();

  thomas = inject(ThomasConversationService);
  private app = inject(ThomasAppConfig);
  private element = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    this.setOpenedStateMode();
    this.fitWidth();
  }

  get opened(): boolean {
    return this.thomas.open && this.thomas.docked;
  }

  get canResize(): boolean {
    return this.openedStateMode === 'shrink';
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.setOpenedStateMode();
    this.fitWidth();
  }

  onOptionChanged(e: OptionChangedEvent) {
    if (e.name !== 'opened') {
      return;
    }
    if (e.value) {
      setTimeout(() => this.chat?.reveal());
      return;
    }
    this.restoreFocus();
    clearTimeout(this.modeTimer);
    this.modeTimer = setTimeout(() => {
      this.setOpenedStateMode();
      this.fitWidth();
    }, this.animationDuration);
  }

  onResizableInitialized(e: InitializedEvent) {
    e.component?.on('resize', () => {
      if (this.opened) {
        this.refreshDrawer();
      }
    });
  }

  onResizeEnd(e: ResizeEndEvent) {
    this.width = this.preferredWidth = e.width;
    try {
      localStorage.setItem(WIDTH_KEY, String(e.width));
    } catch {
      return;
    }
  }

  private setOpenedStateMode() {
    if (!this.opened) {
      this.openedStateMode = window.innerWidth >= this.app.dockMinPageWidth + this.defaultWidth ? 'shrink' : 'overlap';
    }
  }

  private fitWidth() {
    this.maxWidth = Math.min(this.widthCap, Math.max(this.defaultWidth, window.innerWidth - this.app.dockMinPageWidth));
    const width = this.canResize ? Math.min(Math.max(this.preferredWidth, this.minWidth), this.maxWidth) : this.defaultWidth;
    if (width !== this.width) {
      this.width = width;
      this.resizable?.instance.option('width', width);
      this.refreshDrawer();
    }
  }

  private refreshDrawer() {
    const element = this.drawer?.instance.element();
    if (element) {
      trigger(element, 'dxresize');
    }
  }

  private storedWidth(): number {
    try {
      return Number(localStorage.getItem(WIDTH_KEY)) || this.defaultWidth;
    } catch {
      return this.defaultWidth;
    }
  }

  private restoreFocus() {
    if (this.element.nativeElement.querySelector('.thomas-dock')?.contains(document.activeElement)) {
      document.querySelector<HTMLElement>('.thomas-launcher')?.focus();
    }
  }

  newConversation() {
    this.thomas.reset();
    this.chat?.focusComposer();
  }

  ngOnDestroy() {
    clearTimeout(this.modeTimer);
  }
}
